import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp } from 'lucide-react';

export default function StrategyTacticsDialog({ 
  open, 
  onClose, 
  strategyTacticsData,
  missions 
}) {
  const maxXP = strategyTacticsData.length > 0 ? Math.max(...strategyTacticsData.map(d => d.xp)) : 1;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="dash-strategytacticsdialog-dialog" className="bg-slate-900 text-white border-slate-800 max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Target className="w-6 h-6 text-purple-400" />
            Strategy & Tactics XP Allocation
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-4 p-4 bg-slate-950/50 rounded-lg border border-slate-800">
            <div>
              <div className="text-sm text-slate-400">Total Nodes</div>
              <div className="text-2xl font-bold text-slate-200">{strategyTacticsData.length}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">Total XP Allocated</div>
              <div className="text-2xl font-bold text-purple-400">
                {strategyTacticsData.reduce((sum, d) => sum + d.xp, 0)}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-400">Total Missions</div>
              <div className="text-2xl font-bold text-slate-200">
                {strategyTacticsData.reduce((sum, d) => 
                  sum + d.missionsByFlag.green + d.missionsByFlag.yellow + d.missionsByFlag.red, 0
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Nodes by XP Allocation
            </h3>
            <div className="space-y-3">
              {strategyTacticsData.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  No strategy or tactics nodes found
                </div>
              ) : (
                strategyTacticsData.map(({ node, xp, missionsByFlag }) => {
                  const totalMissions = missionsByFlag.green + missionsByFlag.yellow + missionsByFlag.red;
                  
                  return (
                    <div key={node.id} className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize border-purple-500/30 text-purple-400">
                              {node.type}
                            </Badge>
                            <span className="text-slate-300 font-medium">{node.title}</span>
                          </div>
                          {node.description && (
                            <p className="text-sm text-slate-500 mt-1">{node.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-400">{xp}</div>
                          <div className="text-xs text-slate-500">XP</div>
                        </div>
                      </div>
                      
                      <div className="h-10 bg-slate-800 rounded-lg overflow-hidden mb-2">
                        <div 
                          className="h-full flex"
                          style={{ width: `${(xp / maxXP) * 100}%`, minWidth: '60px' }}
                        >
                          {missionsByFlag.green > 0 && (
                            <div 
                              className="h-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm"
                              style={{ width: `${(missionsByFlag.green / totalMissions) * 100}%`, minWidth: '30px' }}
                            >
                              {missionsByFlag.green}
                            </div>
                          )}
                          {missionsByFlag.yellow > 0 && (
                            <div 
                              className="h-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm"
                              style={{ width: `${(missionsByFlag.yellow / totalMissions) * 100}%`, minWidth: '30px' }}
                            >
                              {missionsByFlag.yellow}
                            </div>
                          )}
                          {missionsByFlag.red > 0 && (
                            <div 
                              className="h-full bg-red-500 flex items-center justify-center text-white font-bold text-sm"
                              style={{ width: `${(missionsByFlag.red / totalMissions) * 100}%`, minWidth: '30px' }}
                            >
                              {missionsByFlag.red}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                          <span className="text-slate-400">{missionsByFlag.green} green</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-amber-500 rounded"></div>
                          <span className="text-slate-400">{missionsByFlag.yellow} yellow</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-red-500 rounded"></div>
                          <span className="text-slate-400">{missionsByFlag.red} red</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}